import { isWord } from "./chars";

// Only web and email addresses become links. Anything else, such as javascript: or file:, stays
// as text wherever it appears.

const SAFE = /^(?:https?:\/\/[^\s/?#]+[^\s]*|mailto:[^\s@/?#]+@[^\s@/?#]+[^\s]*)$/i;
// Control characters and the characters browsers drop from a URL would let one address look like
// another, so a link containing them is refused. Browsers read a backslash as a slash, so it is
// refused too, and it also ends a bare address so an escape after one stays an escape.
// biome-ignore lint/suspicious/noControlCharactersInRegex: finding control characters is the point
const UNSAFE_CHARS = /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2060-\u2064\ufeff<>"\\]/u;

/** The address as given when it is an http, https or mailto link that is safe to open. Null otherwise. */
export function safeHref(url: string): string | null {
  const href = url.trim();
  if (href.length > 4096 || UNSAFE_CHARS.test(href) || !SAFE.test(href)) return null;
  return href;
}

export function isSafeHref(url: string): boolean {
  return safeHref(url) !== null;
}

const START = /(?:https?:\/\/|mailto:)/iy;
// A backtick ends a bare address too, so `https://a.b` in a sentence is code around an address.
const BODY = /[^\s<>"\\`]+/y;
const TRAILING = new Set([".", ",", ":", ";", "!", "?", "'", '"', "*", "_", "~", "`", "…"]);

/**
 * Where a bare address starting at index i ends, or -1 when none starts there. The address must
 * not follow a letter or digit, and punctuation that ends a sentence is left out of it, as are
 * closing brackets that have no opening one inside the address.
 */
export function autolinkEnd(s: string, i: number, end: number): number {
  if (isWord(s[i - 1]) || s[i - 1] === "/" || s[i - 1] === "@") return -1;
  START.lastIndex = i;
  if (!START.test(s)) return -1;
  BODY.lastIndex = i;
  if (!BODY.test(s)) return -1;
  let stop = Math.min(BODY.lastIndex, end);
  for (;;) {
    const last = s.charAt(stop - 1);
    if (TRAILING.has(last)) {
      stop--;
      continue;
    }
    const pair = last === ")" ? "(" : last === "]" ? "[" : last === "}" ? "{" : null;
    if (pair) {
      const part = s.slice(i, stop);
      const opens = part.split(pair).length - 1;
      const closes = part.split(last).length - 1;
      if (closes > opens) {
        stop--;
        continue;
      }
    }
    break;
  }
  const href = s.slice(i, stop);
  return safeHref(href) ? stop : -1;
}
